import Head from 'next/head';

import { HOMEPAGE_URL } from '../../helpers/globals';
import { buildHomesMarketSeo, homesMarketRobotsDirective } from './homesMarketSeo';
import type { MarketInfo } from './map/types';
import type { ActiveMarket } from './types';

interface MarketplaceSeoHeadProps {
  activeMarkets: ActiveMarket[];
  canonicalPath: string;
  heroPhotoUrl?: string | null;
  listingCount?: number | null;
  marketInfo: MarketInfo;
}

const HUB_CANONICAL = '/homes';

export const MarketplaceSeoHead: React.FC<MarketplaceSeoHeadProps> = ({
  activeMarkets,
  canonicalPath,
  heroPhotoUrl,
  listingCount,
  marketInfo,
}) => {
  const isHubPage = canonicalPath === HUB_CANONICAL;

  const marketDisplayName = marketInfo.displayName || marketInfo.name;
  const currentMarket = activeMarkets.find(
    (m) => m.identifier === marketInfo.identifier || m.displayName === marketDisplayName,
  );
  const stateAbbreviation = currentMarket?.displayState || '';
  const locationText = stateAbbreviation
    ? `${marketDisplayName}, ${stateAbbreviation}`
    : marketDisplayName;
  const robots = homesMarketRobotsDirective(canonicalPath);
  const seo = buildHomesMarketSeo({
    canonicalPath,
    heroPhotoUrl,
    isHubPage,
    listingCount,
    locationText,
    marketDisplayName,
    marketSlug: marketInfo.identifier?.toLowerCase() ?? '',
  });

  let marketListSchema;
  if (isHubPage) {
    marketListSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Opendoor Markets',
      numberOfItems: activeMarkets.length,
      itemListElement: activeMarkets.map((market, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        item: {
          '@type': 'WebPage',
          name: `Homes for sale in ${market.displayName}`,
          url: `${HOMEPAGE_URL}${market.homesPath}`,
        },
      })),
    };
  }

  return (
    <Head>
      <title key="title">{seo.title}</title>
      <meta key="description" name="description" content={seo.description} />
      {robots ? <meta key="robots" name="robots" content={robots} /> : null}
      <link key="canonical" rel="canonical" href={seo.canonicalUrl} />

      <meta key="og:title" property="og:title" content={seo.ogTitle} />
      <meta key="og:description" property="og:description" content={seo.description} />
      <meta key="og:url" property="og:url" content={seo.canonicalUrl} />
      <meta key="og:type" property="og:type" content="website" />
      <meta key="og:site_name" property="og:site_name" content="Opendoor" />
      <meta key="og:image" property="og:image" content={seo.ogImageUrl} />
      <meta key="og:image:secure_url" property="og:image:secure_url" content={seo.ogImageUrl} />
      <meta key="og:image:alt" property="og:image:alt" content={seo.ogImageAlt} />
      <meta key="og:image:width" property="og:image:width" content="1200" />
      <meta key="og:image:height" property="og:image:height" content="630" />

      <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
      <meta key="twitter:url" property="twitter:url" content={seo.canonicalUrl} />
      <meta key="twitter:title" name="twitter:title" content={seo.ogTitle} />
      <meta key="twitter:description" name="twitter:description" content={seo.description} />
      <meta key="twitter:image" name="twitter:image" content={seo.ogImageUrl} />
      <meta key="twitter:image:alt" name="twitter:image:alt" content={seo.ogImageAlt} />

      {marketListSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(marketListSchema) }}
        />
      )}
    </Head>
  );
};
